import moment, { Moment } from 'moment-timezone';
import { Timestamp } from 'firebase/firestore';

import { triggersFactory } from '@src/packages/schedules/constants';
import {
  getScheduleTimestampMillis,
  haveSameWallClock,
  isScheduleRecurrent,
  resolveScheduleEndAt,
} from '@src/packages/schedules/screens/Schedule/utils';

import { TEventTypeSummary } from '@src/models/interfaces/eventTypeSummary.interface';
import type { TLocationGroup } from '@src/models/interfaces/locationGroup.interface';
import type {
  ScheduleElasticSummarySource,
  ScheduleFormSaveContext,
  ScheduleSaveInput,
  ScheduleSearchSummary,
} from '@src/models/interfaces/schedule.interface';
import TSchedule, { ScheduleType } from '@src/models/interfaces/schedule.interface';
import TScheduleTrigger from '@src/models/interfaces/scheduleTrigger.interface';
import TRepetition from '@src/models/interfaces/repetition.interface';
import type {
  ElasticSearchHit,
  ElasticSearchResponse,
} from '@src/models/interfaces/general/ElasticSearchResponseInterface';
import { EmbeddedAuditMapper } from '@src/models/mappers/commons/embeddedAuditMapper';
import { DateUtil } from '@src/packages/utils/dates';

const SCHEDULE_TYPE_VALUES = Object.values(ScheduleType);

interface ToSaveInputParams {
  scheduleId: string;
  divisionId: string;
  formContext: ScheduleFormSaveContext;
  triggers: TScheduleTrigger[];
  existingSchedule?: TSchedule | null;
  isUpdate?: boolean;
  scheduleType?: ScheduleType;
}

const REPETITION_KEYS: (keyof TRepetition)[] = ['day', 'hour', 'minute', 'month', 'weekDay', 'year'];

/**
 * A trigger targets either explicit `locations` or a `locationGroup`, never both.
 * Mirror of the backend's `hasMeaningfulLocationGroup`
 * (services/src/services/elasticsearch/triggers/normalizeTriggerForElasticsearch.ts):
 * a group only counts when it is an object carrying a non-empty `id`.
 */
const hasMeaningfulLocationGroup = (
  locationGroup: TScheduleTrigger['locationGroup'] | null | undefined,
): locationGroup is TLocationGroup =>
  locationGroup != null &&
  typeof locationGroup === 'object' &&
  String(locationGroup.id ?? '').trim().length > 0;

type EventTypeFirestoreRecord = TEventTypeSummary & {
  uid?: string;
  createdAt?: unknown;
  updatedAt?: unknown;
};

export interface ScheduleElasticIdSource {
  id?: string;
  /** The schedules index exposes the owning division as `divId` — the only allowed name. */
  divId?: string;
}

export interface ScheduleSearchIdHit {
  id: string;
  divisionId: string;
}

export interface ScheduleSearchIdsResult {
  items: ScheduleSearchIdHit[];
  total: number;
}

export class ScheduleMapper {
  static normalizeRepetition(repetition: TRepetition): TRepetition {
    return REPETITION_KEYS.reduce<TRepetition>((acc, key) => {
      const values = repetition?.[key];
      acc[key] = Array.isArray(values) ? values.map(value => Number(value)) : [];
      return acc;
    }, {
      day: [],
      hour: [],
      minute: [],
      month: [],
      weekDay: [],
      year: [],
    });
  }

  static toSaveTrigger(trigger: TScheduleTrigger): TScheduleTrigger {
    const {
      newOrExistingTrigger: _newOrExistingTrigger,
      triggerId,
      ...rest
    } = trigger as TScheduleTrigger & { newOrExistingTrigger?: unknown };

    const normalized: TScheduleTrigger = {
      ...rest,
      id: rest.id,
      name: rest.name,
      cooldown: Number(rest.cooldown) || 0,
      description: rest.description ?? '',
      expectedExecutions: Number(rest.expectedExecutions) || 1,
      avoidConsecutiveTriggering: Boolean(rest.avoidConsecutiveTriggering),
      // Audit fields on embedded location copies must be real Timestamps (or absent):
      // serialized maps from Elastic/callable round-trips corrupt the persisted schedule.
      locations: (rest.locations ?? []).map(location =>
        EmbeddedAuditMapper.normalizeAuditTimestamps(location),
      ),
      // When the user picked explicit locations there is no group: persist `null`
      // instead of an empty object so the field is explicitly cleared (admin SDK
      // `set({ merge: true })` would otherwise keep a stale group on update).
      locationGroup: hasMeaningfulLocationGroup(rest.locationGroup)
        ? EmbeddedAuditMapper.normalizeAuditTimestamps(rest.locationGroup)
        : null,
    };

    if (triggerId) {
      normalized.triggerId = triggerId;
    }

    if (rest.index !== undefined) {
      normalized.index = rest.index;
    }

    if (rest.locationOptional !== undefined) {
      normalized.locationOptional = rest.locationOptional;
    }

    if (rest.locationRequired !== undefined) {
      normalized.locationRequired = rest.locationRequired;
    }

    if (rest.eventType) {
      normalized.eventType = ScheduleMapper.toEventTypeSummary(
        rest.eventType as EventTypeFirestoreRecord,
      );
    }

    return normalized;
  }

  static resolveScheduleTimestamps({
    startAt,
    endAt,
    timeZone,
    duration,
    existingSchedule,
    isUpdate,
  }: {
    startAt: Moment;
    endAt: Moment;
    timeZone: string;
    duration: number;
    existingSchedule?: TSchedule | null;
    isUpdate?: boolean;
  }): { startAt: Timestamp; endAt: Timestamp } {
    const startAtTz = moment(startAt).tz(timeZone, true).seconds(0);
    const endAtTz = moment(endAt).tz(timeZone, true).seconds(0);

    if (!isUpdate || !existingSchedule?.schedule) {
      return {
        startAt: Timestamp.fromMillis(startAtTz.valueOf()),
        endAt: Timestamp.fromMillis(
          Math.max(endAtTz.valueOf(), startAtTz.valueOf() + duration * 60 * 1000),
        ),
      };
    }

    const existingStartMs = getScheduleTimestampMillis(existingSchedule.schedule.startAt);
    const existingEndMs = getScheduleTimestampMillis(existingSchedule.schedule.endAt);

    if (existingStartMs === null || existingEndMs === null) {
      return {
        startAt: Timestamp.fromMillis(startAtTz.valueOf()),
        endAt: Timestamp.fromMillis(
          Math.max(endAtTz.valueOf(), startAtTz.valueOf() + duration * 60 * 1000),
        ),
      };
    }

    const existingStart = moment(existingStartMs).tz(timeZone);
    const existingEnd = moment(existingEndMs).tz(timeZone);

    const startChanged = !haveSameWallClock(startAtTz, existingStart);
    const endChanged = !haveSameWallClock(endAtTz, existingEnd);
    const durationChanged = existingSchedule.duration !== duration;

    let resolvedStartMs = startChanged ? startAtTz.valueOf() : existingStartMs;
    let resolvedEndMs = endChanged ? endAtTz.valueOf() : existingEndMs;

    // When the user did not change dates or duration, keep persisted millis to avoid spurious job rebuilds.
    if (!startChanged && !endChanged && !durationChanged) {
      resolvedStartMs = existingStartMs;
      resolvedEndMs = existingEndMs;
    }

    const minEndMs = resolvedStartMs + duration * 60 * 1000;
    if (resolvedEndMs < minEndMs) {
      resolvedEndMs = minEndMs;
    }

    if (resolvedEndMs <= resolvedStartMs) {
      resolvedEndMs = minEndMs;
    }

    return {
      startAt: Timestamp.fromMillis(resolvedStartMs),
      endAt: Timestamp.fromMillis(resolvedEndMs),
    };
  }

  static toSaveInput({
    scheduleId,
    divisionId,
    formContext,
    triggers,
    existingSchedule = null,
    isUpdate = false,
    scheduleType = ScheduleType.Schedule,
  }: ToSaveInputParams): ScheduleSaveInput {
    const { values, members, supervisors, startAt, endDateEnabled, isRecurrent } = formContext;
    const recurrent = isRecurrent || isScheduleRecurrent(values.repetition);

    const endAt = resolveScheduleEndAt({
      startAt,
      isRecurrent: recurrent,
      endDate: values.endDate,
      duration: values.duration,
      isEndDateEnabled: endDateEnabled,
      repetition: values.repetition,
      existingSchedule,
      isUpdate,
    });

    const reminders: number[] = [];
    if (+values.reminder) {
      reminders.push(+values.reminder);
    }

    const { startAt: startAtTimestamp, endAt: endAtTimestamp } =
      ScheduleMapper.resolveScheduleTimestamps({
        startAt,
        endAt,
        timeZone: values.timeZone,
        duration: values.duration,
        existingSchedule,
        isUpdate,
      });

    const saveInput: ScheduleSaveInput = {
      id: scheduleId,
      divId: divisionId,
      duration: values.duration,
      members,
      name: values.name.trim(),
      reminders,
      schedule: {
        startAt: startAtTimestamp,
        endAt: endAtTimestamp,
        repetition: ScheduleMapper.normalizeRepetition(values.repetition),
        timeZone: values.timeZone,
      },
      triggers,
      type: scheduleType,
      isExpiredAllowed: existingSchedule?.isExpiredAllowed ?? false,
      triggersFollowIndex: values.triggersFollowIndex ?? false,
      forms: existingSchedule?.forms ?? [],
    };

    // Contract (GUA-272): sent separately from `members`; omitted keeps the stored value.
    const nextSupervisors = supervisors ?? existingSchedule?.supervisors;
    if (nextSupervisors !== undefined) {
      saveInput.supervisors = nextSupervisors;
    }

    const minUsers = values.minUsers ?? existingSchedule?.minUsers;
    if (minUsers !== undefined) {
      saveInput.minUsers = minUsers;
    }

    if (existingSchedule?.expiredAllowedTime !== undefined) {
      saveInput.expiredAllowedTime = existingSchedule.expiredAllowedTime;
    }

    if (existingSchedule?.taskSummary) {
      saveInput.taskSummary = existingSchedule.taskSummary;
    }

    return saveInput;
  }

  static applyTriggersFactory(
    triggers: TScheduleTrigger[],
    triggersFollowIndex?: boolean,
  ): TScheduleTrigger[] {
    return triggersFactory(triggers, triggersFollowIndex ?? false) as TScheduleTrigger[];
  }

  static regenerateTriggerIdsForClone(
    triggers: TScheduleTrigger[],
    newTriggerId: () => string,
  ): TScheduleTrigger[] {
    return triggers.map(trigger => ({
      ...trigger,
      id: newTriggerId(),
    }));
  }

  static toEventTypeSummary(raw: EventTypeFirestoreRecord): TEventTypeSummary {
    const { uid: _uid, createdAt: _createdAt, updatedAt: _updatedAt, ...summary } = raw;
    return summary;
  }

  static toSavedScheduleSummary(input: ScheduleSaveInput, scheduleId: string): TSchedule {
    const now = Timestamp.now();

    return {
      ...input,
      id: scheduleId,
      jobs: {
        start: { cron: '' },
        end: { cron: '' },
        reminders: [],
      },
      uid: '',
      createdAt: now,
      updatedAt: now,
    };
  }

  /**
   * Reduces an Elastic hit to what a list needs to materialize it from Firestore: the
   * document id and the division that owns it. Hits missing either are unusable and dropped.
   */
  static fromElasticSearchIdHit = (
    hit: ElasticSearchHit<ScheduleElasticIdSource>,
  ): ScheduleSearchIdHit | null => {
    const id = hit._source?.id ?? hit._id;
    const divisionId = hit._source?.divId;

    if (!id || !divisionId) return null;

    return { id, divisionId };
  };

  static fromElasticSearchIdsResponse = (
    response: ElasticSearchResponse<ScheduleElasticIdSource> | null,
  ): ScheduleSearchIdsResult => {
    const hits = response?.hits?.hits ?? [];
    const items = hits
      .map(hit => ScheduleMapper.fromElasticSearchIdHit(hit))
      .filter((item): item is ScheduleSearchIdHit => item != null);

    return { items, total: response?.hits?.total?.value ?? items.length };
  };

  static fromElasticSearchSummaryHit = (
    hit: ElasticSearchHit<ScheduleElasticSummarySource>,
  ): ScheduleSearchSummary | null => {
    const source = hit._source ?? {};
    const id = source.id ?? hit._id;
    const divisionId = source.divId;
    const type = SCHEDULE_TYPE_VALUES.find(value => value === source.type);

    if (!id || !divisionId || !type) return null;

    return {
      id,
      divisionId,
      name: source.name?.trim() || id,
      type,
      startAtMs: DateUtil.getTimestampMs(source.schedule?.startAt),
    };
  };

  static fromElasticSearchSummariesResponse = (
    response: ElasticSearchResponse<ScheduleElasticSummarySource> | null,
  ): ScheduleSearchSummary[] =>
    (response?.hits?.hits ?? [])
      .map(hit => ScheduleMapper.fromElasticSearchSummaryHit(hit))
      .filter((item): item is ScheduleSearchSummary => item != null);
}
